import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { usePopoverPosition } from '../../hooks/usePopoverPosition';

export interface InfoTooltipProps {
  text: React.ReactNode;
  className?: string;
  label?: string;
}

const Z_POPOVER = 9997;
const GAP = 6;
const BRIDGE = 10;

const InfoTooltip: React.FC<InfoTooltipProps> = ({ text, className, label }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const placement = usePopoverPosition(open, triggerRef, panelRef, GAP);

  const show = useCallback(() => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
  }, []);

  const hide = useCallback(() => {
    closeTimer.current = setTimeout(() => setOpen(false), 300);
  }, []);

  return (
    <span className={className}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        onClick={e => {
          e.stopPropagation();
          setOpen(o => !o);
        }}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-gray-900 align-middle"
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && placement && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              role="tooltip"
              onMouseEnter={show}
              onMouseLeave={hide}
              onClick={e => e.stopPropagation()}
              className="fixed"
              style={{ top: placement.top - BRIDGE, left: placement.left, zIndex: Z_POPOVER, paddingTop: BRIDGE }}
            >
              <div className="max-w-xs px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                {text}
              </div>
            </div>,
            document.body
          )
        : null}
    </span>
  );
};

export default InfoTooltip;
